import React, { useEffect, useState } from "react";
import { RefreshCw, ShieldAlert, ShieldCheck, UserCheck, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const RISK_STYLES = {
  clean: "bg-emerald-400/15 border-emerald-300/30 text-emerald-200",
  low: "bg-sky-400/15 border-sky-300/30 text-sky-200",
  medium: "bg-amber-400/15 border-amber-300/40 text-amber-200",
  high: "bg-red-400/15 border-red-300/40 text-red-200",
};

const RiskBadge = ({ alt }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[0.68rem] font-bold tracking-[0.1em] uppercase ${RISK_STYLES[alt.level] || RISK_STYLES.clean}`}
  >
    {alt.level === "clean" ? <ShieldCheck className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />}
    {alt.level} · {alt.score}
  </span>
);

const ageOf = (isoDate) => {
  if (!isoDate) return "-";
  const days = Math.floor((Date.now() - new Date(isoDate).getTime()) / 86400000);
  if (days < 1) return "today";
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${(days / 365).toFixed(1)}y ago`;
};

const StatCard = ({ icon: Icon, label, value, tone = "text-[#7fd0f5]" }) => (
  <div className="glass-card-inner px-5 py-4 flex items-center gap-4">
    <Icon className={`w-6 h-6 ${tone}`} />
    <div>
      <p className="font-display font-bold text-white text-[1.4rem] leading-none">{value}</p>
      <p className="text-[#9fc5e8] text-[0.7rem] font-bold tracking-[0.18em] uppercase mt-1">{label}</p>
    </div>
  </div>
);

const AdminUsers = () => {
  const [users, setUsers] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState(null);

  const load = () => {
    api
      .get("/admin/users")
      .then((res) => setUsers(res.data.users))
      .catch(() => {
        toast.error("Failed to load users");
        setUsers([]);
      });
  };

  useEffect(load, []);

  const recheckAll = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/recheck-all");
      toast.success(`Re-checked ${res.data.rechecked} user(s)`);
      load();
    } catch (e) {
      toast.error("Recheck failed");
    } finally {
      setBusy(false);
    }
  };

  const recheckOne = async (u) => {
    setRowBusy(u.id);
    try {
      const res = await api.post(`/admin/users/${u.id}/recheck`);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? res.data.user : x)));
      toast.success(`Re-checked @${u.username}`);
    } catch (e) {
      toast.error("Recheck failed");
    } finally {
      setRowBusy(null);
    }
  };

  if (users === null) {
    return (
      <div className="glass-card py-16 flex justify-center">
        <p className="text-[#bcd9f2]">Loading users...</p>
      </div>
    );
  }

  const flagged = users.filter((u) => u.alt.level === "medium" || u.alt.level === "high").length;
  const joined = users.filter((u) => u.guild_joined).length;

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={UsersIcon} label="Discord signups" value={users.length} />
        <StatCard icon={UserCheck} label="Joined server" value={joined} tone="text-emerald-300" />
        <StatCard icon={ShieldAlert} label="Flagged as possible alts" value={flagged} tone="text-amber-300" />
      </div>

      <div className="glass-card mt-6 p-5 sm:p-7">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h3 className="font-display font-bold text-white text-[1.2rem]">Signed-up users</h3>
          <button type="button" onClick={recheckAll} disabled={busy} className="btn-pill btn-blue !py-2.5 !px-5 text-[0.72rem] disabled:opacity-60">
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
            Redo all checks
          </button>
        </div>

        {users.length === 0 ? (
          <p className="mt-8 text-center text-[#9fc5e8] text-[0.92rem] pb-4">No Discord signups yet.</p>
        ) : (
          <div className="mt-5 overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[860px]">
              <thead>
                <tr className="text-[#9fc5e8] text-[0.66rem] font-bold tracking-[0.18em] uppercase border-b border-white/10">
                  <th className="py-3 pr-4">User</th>
                  <th className="py-3 pr-4">Discord ID</th>
                  <th className="py-3 pr-4">Account created</th>
                  <th className="py-3 pr-4">Server</th>
                  <th className="py-3 pr-4">Alt risk</th>
                  <th className="py-3 pr-4">Last login</th>
                  <th className="py-3"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-white/5 align-top hover:bg-white/[0.03] transition-colors duration-200">
                    <td className="py-3.5 pr-4">
                      <div className="flex items-center gap-3">
                        <img src={u.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover" />
                        <div>
                          <p className="text-white font-bold text-[0.9rem] leading-tight">{u.global_name || u.username}</p>
                          <p className="text-[#9fc5e8] text-[0.75rem]">@{u.username}{u.is_admin ? " · admin" : ""}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3.5 pr-4 text-[#bcd9f2] text-[0.8rem] font-mono">{u.discord_id}</td>
                    <td className="py-3.5 pr-4 text-[#bcd9f2] text-[0.82rem]">
                      {new Date(u.account_created_at).toLocaleDateString()}
                      <span className="block text-[#7fa8cc] text-[0.72rem]">{ageOf(u.account_created_at)}</span>
                    </td>
                    <td className="py-3.5 pr-4">
                      <span
                        className={`px-2.5 py-1 rounded-full border text-[0.66rem] font-bold tracking-[0.1em] uppercase ${
                          u.guild_joined
                            ? "bg-emerald-400/15 border-emerald-300/30 text-emerald-200"
                            : "bg-white/8 border-white/20 text-[#9fc5e8]"
                        }`}
                      >
                        {u.guild_joined ? "Joined" : "Not joined"}
                      </span>
                    </td>
                    <td className="py-3.5 pr-4 max-w-[260px]">
                      <RiskBadge alt={u.alt} />
                      {u.alt.reasons.length > 0 && (
                        <ul className="mt-1.5 space-y-0.5">
                          {u.alt.reasons.map((r) => (
                            <li key={r} className="text-[#b3d2ee] text-[0.72rem] leading-snug">· {r}</li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className="py-3.5 pr-4 text-[#bcd9f2] text-[0.8rem]">{ageOf(u.last_login)}</td>
                    <td className="py-3.5 text-right">
                      <button
                        type="button"
                        onClick={() => recheckOne(u)}
                        disabled={rowBusy === u.id}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white/8 border border-white/20 text-[#d7eafb] text-[0.7rem] font-bold tracking-[0.1em] uppercase hover:bg-white/15 hover:border-white/35 transition-colors duration-200 disabled:opacity-50"
                      >
                        <RefreshCw className={`w-3 h-3 ${rowBusy === u.id ? "animate-spin" : ""}`} />
                        Recheck
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
